// frontend/admin-portal/src/components/procurement/ReceiveStockModal.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Alert, Badge } from '@fiberhome/shared-ui';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import DefaultAccountBadge from '../treasury/DefaultAccountBadge';

export default function ReceiveStockModal({ open, po, onClose, onReceived }) {
  const [form, setForm] = useState({
    receivedAt: new Date().toISOString().slice(0, 10),
    invoiceNumber: '',
    notes: '',
  });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Reset state on open
  useEffect(() => {
    if (!open || !po) return;
    setForm({
      receivedAt: new Date().toISOString().slice(0, 10),
      invoiceNumber: '',
      notes: '',
    });
    setLines(
      (po.items || [])
        .filter((it) => Number(it.receivedQuantity) < Number(it.quantity))
        .map((it) => ({
          purchaseOrderItemId: it.id,
          productName: it.productName,
          productSku: it.productSku,
          productId: it.productId || null,
          ordered: Number(it.quantity),
          alreadyReceived: Number(it.receivedQuantity || 0),
          remaining: Number(it.quantity) - Number(it.receivedQuantity || 0),
          quantityReceived: 0,
          unitPrice: Number(it.unitPrice) || 0,
          unit: it.unit || 'pcs',
          notes: '',
        }))
    );
    setError('');
    setResult(null);
  }, [open, po]);

  const update = (idx, patch) => {
    const next = [...lines];
    next[idx] = { ...next[idx], ...patch };
    setLines(next);
  };

  const setAllRemaining = () => {
    setLines(lines.map((l) => ({ ...l, quantityReceived: l.remaining })));
  };

  const clearAll = () => {
    setLines(lines.map((l) => ({ ...l, quantityReceived: 0 })));
  };

  const nonEmpty = lines.filter((l) => Number(l.quantityReceived) > 0);

  const hasInvalid = nonEmpty.some(
    (l) => l.quantityReceived > l.remaining || l.quantityReceived < 0
  );

  const canSubmit = nonEmpty.length > 0 && !hasInvalid;

  // Total value being received (used for treasury info)
  const receivedValue = nonEmpty.reduce(
    (sum, l) => sum + Number(l.quantityReceived) * Number(l.unitPrice || 0),
    0
  );

  // Count how many unlinked items → will be auto-created
  const unlinkedCount = nonEmpty.filter((l) => !l.productId).length;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const data = await purchaseOrderService.receive(po.id, {
        receivedAt: form.receivedAt,
        invoiceNumber: form.invoiceNumber || null,
        notes: form.notes || null,
        items: nonEmpty.map((l) => ({
          purchaseOrderItemId: l.purchaseOrderItemId,
          quantityReceived: Number(l.quantityReceived),
          notes: l.notes || null,
        })),
      });
      setResult(data);
      onReceived?.(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Receiving failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setResult(null);
    setError('');
    onClose?.();
  };

  const currency = po?.currency || 'USD';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        result
          ? `Receipt ${result?.receipt?.code || result?.code || ''} Posted`
          : `Receive stock for ${po?.code || ''}`
      }
      size="xl"
      footer={
        result ? (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={saving}
              disabled={!canSubmit}
            >
              Post Receipt ({nonEmpty.length} line{nonEmpty.length === 1 ? '' : 's'})
            </Button>
          </>
        )
      }
    >
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* ========================================================= */}
      {/* RESULT STATE                                              */}
      {/* ========================================================= */}
      {result && (
        <>
          <Alert variant="success" className="mb-3">
            Receipt{' '}
            <strong>{result?.receipt?.code || result?.code || '(no code)'}</strong>{' '}
            posted. Product stock has been incremented and treasury was updated.
          </Alert>

          {result?.autoCreatedProducts?.length > 0 && (
            <div className="mb-3">
              <div className="fh-modal-section-title">
                Auto-created products ({result.autoCreatedProducts.length})
              </div>
              <div className="fh-auto-products">
                {result.autoCreatedProducts.map((p) => (
                  <div key={p.id} className="fh-auto-products__item">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {p.name}
                      </div>
                      <div
                        className="text-muted"
                        style={{ fontSize: 11.5 }}
                      >
                        {p.sku}
                        {p.categoryName && ` · ${p.categoryName}`}
                        {typeof p.attributeCount === 'number' &&
                          ` · ${p.attributeCount} specs`}
                      </div>
                    </div>
                    <Badge variant="success">created</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-muted" style={{ fontSize: 12.5 }}>
            The PO status was updated to reflect the received quantities.
            You can now allocate the landed cost from the PO detail page.
          </div>
        </>
      )}

      {/* ========================================================= */}
      {/* RECEIVING STATE                                            */}
      {/* ========================================================= */}
      {!result && (
        <>
          {/* Header info */}
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <Input
                label="Received on"
                type="date"
                value={form.receivedAt}
                onChange={(e) => setForm({ ...form, receivedAt: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Supplier Invoice #"
                value={form.invoiceNumber}
                onChange={(e) =>
                  setForm({ ...form, invoiceNumber: e.target.value })
                }
                placeholder="optional"
              />
            </div>
            <div className="col-md-4 d-flex flex-column justify-content-end">
              <div className="d-flex gap-2">
                <Button size="sm" variant="ghost" onClick={setAllRemaining}>
                  Receive all
                </Button>
                <Button size="sm" variant="ghost" onClick={clearAll}>
                  Clear
                </Button>
              </div>
            </div>
            <div className="col-12">
              <Input
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          {/* Info panel */}
          <div className="fh-receive-info mb-3">
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Receiving value
              </div>
              <strong>
                {currency} {receivedValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Lines to receive
              </div>
              <strong>
                {nonEmpty.length} / {lines.length}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Unlinked items (will auto-create)
              </div>
              <strong>
                {unlinkedCount > 0 ? (
                  <Badge variant="warning">{unlinkedCount}</Badge>
                ) : (
                  <span className="text-muted">0</span>
                )}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                Treasury account
              </div>
              <DefaultAccountBadge />
            </div>
          </div>

          {unlinkedCount > 0 && (
            <Alert variant="info" className="mb-3">
              <strong>{unlinkedCount} line{unlinkedCount === 1 ? '' : 's'}</strong>{' '}
              {unlinkedCount === 1 ? 'has' : 'have'} no product in the catalog
              yet. On posting, a new product will be created for each with:
              <ul className="mt-2 mb-0">
                <li>Auto-detected category from the product name</li>
                <li>Parsed specifications (connectors, fiber mode, length, etc.)</li>
                <li>SKU from the PO (or auto-generated)</li>
                <li>Cost = PO unit price (freight allocation can be applied after)</li>
              </ul>
            </Alert>
          )}

          {/* Lines table */}
          {lines.length === 0 ? (
            <Alert variant="success">
              All items on this PO have been fully received.
            </Alert>
          ) : (
            <div className="fh-recv-lines">
              <div className="fh-recv-lines__head">
                <div>Item</div>
                <div className="text-right">Ordered</div>
                <div className="text-right">Received</div>
                <div className="text-right">Remaining</div>
                <div className="text-right">Unit Price</div>
                <div className="text-center">Receiving now</div>
              </div>
              {lines.map((l, idx) => {
                const invalid =
                  l.quantityReceived > l.remaining || l.quantityReceived < 0;
                return (
                  <div
                    className={`fh-recv-lines__row ${invalid ? 'is-invalid' : ''}`}
                    key={l.purchaseOrderItemId}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{l.productName}</div>
                      <div className="text-muted" style={{ fontSize: 11.5 }}>
                        {l.productSku || <em>(no SKU)</em>}
                        {l.productId ? (
                          <span style={{ color: 'var(--fh-success)' }}>
                            {' '}· linked
                          </span>
                        ) : (
                          <span style={{ color: 'var(--fh-warning)' }}>
                            {' '}· will auto-create
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {l.ordered} {l.unit}
                    </div>
                    <div className="text-right">{l.alreadyReceived}</div>
                    <div className="text-right">
                      <strong>{l.remaining}</strong>
                    </div>
                    <div className="text-right">
                      {currency} {Number(l.unitPrice).toFixed(2)}
                    </div>
                    <div className="text-center">
                      <input
                        className={`fh-input fh-recv-input ${
                          invalid ? 'is-invalid' : ''
                        }`}
                        type="number"
                        min={0}
                        max={l.remaining}
                        step={1}
                        value={l.quantityReceived}
                        onChange={(e) =>
                          update(idx, {
                            quantityReceived: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasInvalid && (
            <Alert variant="danger" className="mt-3">
              One or more lines exceed the remaining quantity. Reduce them
              before posting.
            </Alert>
          )}
        </>
      )}
    </Modal>
  );
}